<template>
  <div class="admin-month-view">
    <div class="header">
      <div class="left-section">
        <button @click="navigateTo('main')" class="btn-volver">
          <AppIcon :src="require('@/modules/working_hours/assets/keyboard_backspace.svg')" :size="29" class="icono-btn-secu" />
          Vista general
        </button>
        <h1 class="page-title">Previsualización mes</h1>
      </div>

      <div class="right-section">
        <button
          @click="loadUserData"
          class="btn-primario btn-refresh"
          :disabled="screenData.ui_loading"
        >
          <AppIcon :src="require('@/modules/working_hours/assets/refresh.svg')" :size="29" class="icono-btn" />
        </button>
      </div>
    </div>
    <hr class="header-separator" />

    <div class="acciones-secundarias">
      <button @click="navigateTo('absences')" class="btn-secundario-1">
        Ausencias
      </button>
      <button @click="navigateTo('history')" class="btn-secundario-1">
        Historial
      </button>
      <button class="btn-secundario">
        Previsualización mes
      </button>
      <button @click="navigateTo('holidays')" class="btn-secundario-1">
        Vacaciones
      </button>
      <button
        v-if="canViewAdminButton"
        @click="navigateToAdmin"
        class="btn-secundario-1"
      >
        Admin
      </button>
    </div>
    <hr class="header-separator-1" />

  <div class="header-controls">
    <div class="user-selector">
      <v-select
        v-model="selectedUser"
        :items="screenData.users"
        item-text="username"
        item-value="id"
        label="Seleccionar Usuario"
        outlined
        dense
        class="user-dropdown"
        @change="onUserChange"
      ></v-select>
    </div>

    <v-btn
      color="primary"
      outlined
      @click="downloadCSV"
      :disabled="!selectedUser"
      class="download-csv-btn"
    >
      <v-icon left>mdi-download</v-icon>
      Descargar CSV
    </v-btn>

    <div class="month-selector">
      <v-btn icon @click="previousMonth" class="month-nav-btn">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      
      <div class="month-display">
        {{ getMonthName(screenData.currentMonth) }} {{ screenData.currentYear }}
      </div>
      
      <v-btn icon @click="nextMonth" class="month-nav-btn">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    </div>

    <hr class="section-separator" />

    <!-- Widgets de resumen -->
    <div class="summary-widgets-container" :class="{ 'full-width-widgets': !activeBaja && !hasPendingBajas }" v-if="selectedUser">
      <!-- Widget de baja activa (solo si hay una baja en curso) -->
      <div class="active-baja-widget" v-if="activeBaja">
        <div class="widget-header-baja-active">
          <v-icon color="#F44336" left size="32">mdi-hospital-box</v-icon>
          <h3 class="widget-title-baja-active">De baja actualmente</h3>
        </div>
        <div class="baja-active-content">
          <div class="baja-active-row">
            <span class="baja-active-label">Tipo:</span>
            <span class="baja-active-value">{{ formatBajaType(activeBaja.tipoAusencia) }}</span>
          </div>
          <div class="baja-active-row">
            <span class="baja-active-label">Desde:</span>
            <span class="baja-active-value">{{ formatOnlyDate(activeBaja.start) }}</span>
          </div>
          <div class="baja-active-row">
            <span class="baja-active-label">Hasta:</span>
            <span class="baja-active-value" v-if="!isIndefinidaBaja(activeBaja)">{{ formatOnlyDate(activeBaja.end) }}</span>
            <div v-else class="editable-date-container">
              <v-menu
                ref="activeBajaDateMenu"
                v-model="activeBaja.showDatePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="activeBaja.editedEndDate || formatOnlyDate(activeBaja.end)"
                    placeholder="Seleccionar fecha"
                    readonly
                    dense
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    hide-details
                    class="compact-date-field"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :value="activeBaja.editedEndDatePicker || formatDateForPicker(activeBaja.end)"
                  @input="onActiveBajaEndDateChange($event)"
                  :first-day-of-week="1"
                  locale="es"
                  no-title
                  scrollable
                  :min="formatDateForPicker(activeBaja.start)"
                ></v-date-picker>
              </v-menu>
            </div>
          </div>
          <div class="baja-active-status">
            <v-icon color="#F44336" left small>mdi-information</v-icon>
            <span>Este empleado está de baja</span>
          </div>
          <v-btn
            v-if="isIndefinidaBaja(activeBaja) && activeBaja.editedEndDatePicker"
            color="primary"
            small
            @click="updateActiveBajaEndDate"
            :loading="activeBaja.updating"
            class="update-baja-btn"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar nueva fecha
          </v-btn>
        </div>
      </div>

      <div class="summary-widgets" :class="{ 'summary-widgets-horizontal': !canViewAdminButton }">
        <div class="widget">
          <div class="widget-icon">
            <v-icon color="primary">mdi-clock-outline</v-icon>
          </div>
          <div class="widget-content">
            <div class="widget-label">Horas trabajadas este mes</div>
            <div class="widget-value">{{ totalWorkedHours }}</div>
          </div>
        </div>

        <div class="widget">
          <div class="widget-icon">
            <v-icon :color="extraHoursColor">mdi-timer-sand</v-icon>
          </div>
          <div class="widget-content">
            <div class="widget-label">Balance de horas extras</div>
            <div class="widget-value" :style="{ color: extraHoursColor }">{{ extraHoursText }}</div>
          </div>
        </div>
      </div>

      <!-- Contenedor para vacaciones y bajas (solo para admins) -->
      <div v-if="canViewAdminButton" class="admin-widgets-container">
        <!-- Widget de vacaciones solicitadas -->
        <div class="holidays-widget">
          <div class="widget-header-holidays">
            <v-icon color="#7339B7" left>mdi-beach</v-icon>
            <h3 class="widget-title-holidays">Vacaciones solicitadas</h3>
          </div>
          <div v-if="pendingHolidays.length > 0" class="holidays-list">
            <div v-for="holiday in pendingHolidays" :key="holiday.id" class="holiday-card">
            <div class="holiday-info">
              <div class="holiday-row">
                <span class="holiday-label">Desde:</span>
                <span class="holiday-value">{{ formatOnlyDate(holiday.start) }}</span>
              </div>
              <div class="holiday-row">
                <span class="holiday-label">Hasta:</span>
                <span class="holiday-value">{{ formatOnlyDate(holiday.end) }}</span>
              </div>
              <div class="holiday-row">
                <span class="holiday-label">Motivo:</span>
                <span class="holiday-value">{{ holiday.reason || 'Sin especificar' }}</span>
              </div>
            </div>
            <div class="holiday-actions">
              <v-btn
                small
                color="success"
                @click="approveHoliday(holiday)"
                :loading="holiday.approving"
                class="action-btn"
              >
                <v-icon left small>mdi-check</v-icon>
                Aprobar
              </v-btn>
              <v-btn
                small
                color="error"
                @click="rejectHoliday(holiday)"
                :loading="holiday.rejecting"
                class="action-btn"
              >
                <v-icon left small>mdi-close</v-icon>
                Rechazar
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else class="no-holidays-message">
          <v-icon size="48" color="#7339B7">mdi-beach</v-icon>
          <p>No hay peticiones de vacaciones pendientes</p>
        </div>
      </div>

      <!-- Widget de bajas solicitadas (solo si hay bajas pendientes) -->
      <div class="holidays-widget" v-if="pendingBajas.length > 0">
        <div class="widget-header-holidays">
          <v-icon color="#F44336" left>mdi-medical-bag</v-icon>
          <h3 class="widget-title-holidays">Bajas solicitadas</h3>
        </div>
        <div class="holidays-list">
          <div v-for="baja in pendingBajas" :key="baja.id" class="holiday-card">
          <div class="holiday-info">
            <div class="holiday-row">
              <span class="holiday-label">Tipo:</span>
              <span class="holiday-value">{{ formatBajaType(baja.tipoAusencia) }}</span>
            </div>
            <div class="holiday-row">
              <span class="holiday-label">Desde:</span>
              <span class="holiday-value">{{ formatOnlyDate(baja.start) }}</span>
            </div>
            <div class="holiday-row">
              <span class="holiday-label">Hasta:</span>
              <span class="holiday-value">{{ formatBajaEndDate(baja) }}</span>
            </div>
            <div class="holiday-row" v-if="baja.reason">
              <span class="holiday-label">Anotaciones:</span>
              <span class="holiday-value">{{ baja.reason || 'Sin especificar' }}</span>
            </div>
          </div>
          <div class="holiday-actions">
            <v-btn
              small
              color="success"
              @click="approveBaja(baja)"
              :loading="baja.approving"
              class="action-btn"
            >
              <v-icon left small>mdi-check</v-icon>
              Aprobar
            </v-btn>
            <v-btn
              small
              color="error"
              @click="rejectBaja(baja)"
              :loading="baja.rejecting"
              class="action-btn"
            >
              <v-icon left small>mdi-close</v-icon>
              Rechazar
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>

    <div class="calendar-container">
      <div class="calendar-header">
        <div v-for="day in dayNames" :key="day" class="day-header">
          {{ day }}
        </div>
      </div>
      
      <div class="calendar-body">
        <div 
          v-for="(week, weekIndex) in screenData.monthInfo" 
          :key="weekIndex" 
          class="calendar-week"
        >
          <div
            v-for="(day, dayIndex) in week"
            :key="dayIndex"
            class="calendar-day"
            :class="{
              'empty-day': day.isEmpty,
              'current-day': isToday(day.date),
              'clickable-day': !day.isEmpty || day.date,
              'holiday-approved-day': hasApprovedHolidayOnDate(day.date)
            }"
            @click="day.date ? openDayPopup(day) : null"
          >
            <div class="day-number">
              {{ getDayNumber(day.date) }}
            </div>
            <div class="day-hours" v-if="!day.isEmpty && day.totalMinutes > 0">
              {{ formatHoursAndMinutes(day.totalMinutes) }}
            </div>
            <div class="day-details" v-if="!day.isEmpty && day.clocks.length > 0">
              <div
                v-for="(clock, clockIndex) in day.clocks"
                :key="clockIndex"
                class="clock-entry"
              >
                {{ clock.clockIn }} - {{ clock.clockOut }}
              </div>
            </div>

            <div v-if="hasAbsenceOnDate(day.date)" class="absence-indicator">
              <div class="absence-dot" :class="{
                'absence-dot-holiday-approved': hasApprovedHolidayOnDate(day.date),
                'absence-dot-holiday-pending': hasPendingHolidayOnDate(day.date),
                'absence-dot-approved': hasApprovedAbsenceOnDate(day.date),
                'absence-dot-pending': hasPendingAbsenceOnDate(day.date)
              }"></div>
            </div>

            <div v-if="!day.isEmpty" class="click-indicator">
              <v-icon small>mdi-pencil</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AdminPopup
      :visible="popupVisible"
      :selected-date="selectedDate"
      :clocks="selectedDayClocks"
      :absences="selectedDayAbsences"
      :selected-user-id="selectedUser"
      :users="screenData.users"
      :current-user="currentUser"
      @close="closePopup"
      @saved="onClocksSaved"
      @approve-change="onApproveChange"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      <b>{{ snackbar.text }}</b>
    </v-snackbar>
  </div>
</template>


<script>
import { WorkingHoursAdminMonthComponent } from "@/modules/working_hours/app/WorkingHoursAdminMonthComponent";
import { VueWorkingHoursAdminMonthView } from "@/modules/working_hours/infra/VueWorkingHoursAdminMonthView";
import AdminPopup from "./AdminPopup.vue";
import { APIClockRepository } from '@/modules/working_hours/infra/APIClockRepository';
import { APIUserRepo } from '@/modules/working_hours/infra/APIUserRepo';
import { APIAbsenceRepository } from '@/modules/working_hours/infra/APIAbsenceRepository';

export default {
  name: "AdminMonthView",
  components: {
    AdminPopup
  },
  props: {
    companydb_id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      screenData: {
        users: [],
        monthInfo: [],
        currentMonth: new Date().getMonth() + 1,
        currentYear: new Date().getFullYear(),
        ui_loading: false,
        absences: [],
        workSchedule: null
      },
      currentUser: null,
      selectedUser: null,
      dayNames: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
      monthNames: [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
      ],
      workingHoursComponent: null,
      vueMonthView: null, // Nueva instancia para cálculos
      absenceRepo: null,
      popupVisible: false,
      selectedDate: null,
      selectedDayClocks: [],
      selectedDayAbsences: [],
      // Snackbar
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      }
    };
  },

  async mounted() {
    this.initializeComponent();
    await this.loadUsers();
    this.loadCurrentUser();
  },

  computed: {
    canViewAdminButton() {
      const userData = this.$store.getters['me/userData'];
      if (!userData) return false;

      const userRole = userData.user_role ? userData.user_role.value : null;
      return userRole === 'owner' || userRole === 'admin' || userRole === 'customer';
    },

    totalWorkedMinutes() {
      return this.vueMonthView ? this.vueMonthView.calculateTotalWorkedMinutes() : 0;
    },

    totalWorkedHours() {
      return this.vueMonthView ? this.vueMonthView.calculateTotalWorkedHours() : '0h';
    },

    expectedMinutes() {
      if (!this.screenData.workSchedule || !this.screenData.workSchedule.horario_semanal) {
        return 0;
      }

      const horario = this.screenData.workSchedule.horario_semanal;
      const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

      let minutosEsperados = 0;
      const year = this.screenData.currentYear;
      const month = this.screenData.currentMonth - 1; // JavaScript month is 0-indexed

      const hoy = new Date();
      const mesActual = hoy.getMonth();
      const anioActual = hoy.getFullYear();

      let ultimoDiaAContar;
      if (year === anioActual && month === mesActual) {
        ultimoDiaAContar = hoy.getDate();
      } else {
        ultimoDiaAContar = new Date(year, month + 1, 0).getDate();
      }

      for (let dia = 1; dia <= ultimoDiaAContar; dia++) {
        const fecha = new Date(year, month, dia);
        const diaSemanaIndex = fecha.getDay(); // 0=domingo, 1=lunes, etc
        const nombreDia = diasSemana[diaSemanaIndex];
        const horarioDia = horario[nombreDia];

        // Si el día tiene horario configurado (hora_entrada y hora_salida no son null)
        if (horarioDia && horarioDia.hora_entrada && horarioDia.hora_salida) {
          // Verificar si hay ausencia aprobada este día
          const fechaStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const tieneAusenciaAprobada = this.isDayWithApprovedAbsence(fechaStr);

          // Solo contar si NO tiene ausencia aprobada
          if (!tieneAusenciaAprobada) {
            const [horaIn, minIn] = horarioDia.hora_entrada.split(':').map(Number);
            const [horaOut, minOut] = horarioDia.hora_salida.split(':').map(Number);
            const descanso = horarioDia.descanso || 0;
            const minutosDelDia = (horaOut * 60 + minOut) - (horaIn * 60 + minIn) - descanso;
            minutosEsperados += minutosDelDia;
          }
        }
      }

      return minutosEsperados;
    },

    // CÁLCULO DEL BALANCE: Horas trabajadas - Horas esperadas
    extraMinutes() {
      return this.totalWorkedMinutes - this.expectedMinutes;
    },

    // TEXTO DEL BALANCE: Muestra si tiene horas extras (+) o debe horas (-)
    extraHoursText() {
      const totalMinutes = Math.abs(this.extraMinutes);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      let text = '';
      if (hours === 0) {
        text = `${minutes} min`;
      } else if (minutes === 0) {
        text = `${hours}h`;
      } else {
        text = `${hours}h ${minutes}min`;
      }

      // Agregar signo positivo o negativo
      if (this.extraMinutes > 0) {
        return `+${text}`;
      } else if (this.extraMinutes < 0) {
        return `-${text}`;
      } else {
        return text;
      }
    },

    // COLOR DEL BALANCE: Verde si tiene extras, rojo si debe horas, morado si está al día
    extraHoursColor() {
      if (this.extraMinutes > 0) {
        return '#4CAF50'; // Verde - tiene horas extras
      } else if (this.extraMinutes < 0) {
        return '#F44336'; // Rojo - debe horas
      } else {
        return '#7339B7'; // Morado - está al día
      }
    },

    pendingHolidays() {
      if (!this.selectedUser || !this.screenData.absences) return [];

      // Filtrar vacaciones del usuario seleccionado que sean del mes actual
      const startOfMonth = new Date(this.screenData.currentYear, this.screenData.currentMonth - 1, 1);
      const endOfMonth = new Date(this.screenData.currentYear, this.screenData.currentMonth, 0, 23, 59, 59);

      return this.screenData.absences.filter(absence => {
        // Filtrar solo vacaciones
        if (absence.tipoAusencia !== 'Vacaciones') return false;

        // Filtrar solo pendientes
        if (absence.status !== 'pending') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);

        // Verificar si la ausencia está en el mes actual
        return (absenceStart <= endOfMonth && absenceEnd >= startOfMonth);
      });
    },

    pendingBajas() {
      if (!this.selectedUser || !this.screenData.absences) return [];

      // Filtrar bajas del usuario seleccionado que sean del mes actual
      const startOfMonth = new Date(this.screenData.currentYear, this.screenData.currentMonth - 1, 1);
      const endOfMonth = new Date(this.screenData.currentYear, this.screenData.currentMonth, 0, 23, 59, 59);

      return this.screenData.absences.filter(absence => {
        // Filtrar solo bajas (tipos que empiezan con "Baja:")
        if (!absence.tipoAusencia || !absence.tipoAusencia.startsWith('Baja:')) return false;

        // Filtrar solo pendientes
        if (absence.status !== 'pending') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);

        // Verificar si la baja está en el mes actual
        return (absenceStart <= endOfMonth && absenceEnd >= startOfMonth);
      });
    },

    hasPendingBajas() {
      return this.pendingBajas.length > 0;
    },

    activeBaja() {
      if (!this.selectedUser || !this.screenData.absences) return null;

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Buscar baja activa (aprobada y que incluya el día de hoy)
      return this.screenData.absences.find(absence => {
        // Filtrar solo bajas
        if (!absence.tipoAusencia || !absence.tipoAusencia.startsWith('Baja:')) return false;

        // Filtrar solo aprobadas
        if (absence.status !== 'approved') return false;

        // Verificar si hoy está dentro del rango de la baja
        const absenceStart = absence.start.split('T')[0];
        const absenceEnd = absence.end.split('T')[0];

        return todayStr >= absenceStart && todayStr <= absenceEnd;
      });
    }
  },

  watch: {
    companydb_id: async function (newVal, oldVal) {
      if (newVal !== oldVal) {
        await this.$router.push({
          name: 'working-hours-admin-month',
          params: { companydb_id: newVal }
        }).catch(() => {});
        // Recargar los datos con la nueva empresa
        await this.loadUsers();
      }
    }
  },

  methods: {
    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    getCompanyId() {
      return this.companydb_id || this.$route.params.companydb_id || (this.$store.getters["me/userData"] && this.$store.getters["me/userData"].company_id) || "1";
    },
    navigateTo(section) {
      const companyId = this.getCompanyId();
      const routes = {
        "main": `/${companyId}/working-hours`,
        "absences": `/${companyId}/working-hours-absences`,
        "holidays": `/${companyId}/working-hours-holidays`,
        "history": `/${companyId}/working-hours-history`,
        "admin-month": `/${companyId}/working-hours-admin-month`,
        "admin-users": `/${companyId}/working-hours-admin-users`
      };
      var result = this.$router.push(routes[section]);
      if (result && result.catch) {
        result.catch(function() {});
      }
    },
    navigateToAdmin() {
      // Todos (owner, admin, cliente) van a working-hours-admin-users
      this.navigateTo('admin-users');
    },
    initializeComponent() {
      const clockRepo = new APIClockRepository();
      const userRepo = new APIUserRepo(this.$store);
      this.absenceRepo = new APIAbsenceRepository();

      this.workingHoursComponent = new WorkingHoursAdminMonthComponent(
        this.screenData,
        clockRepo,
        userRepo
      );

      // Inicializar el adaptador Vue para cálculos
      this.vueMonthView = new VueWorkingHoursAdminMonthView(this.screenData);
    },

    async handleSaved() {
      // Recargar los datos del usuario
      await this.loadUserData();
      this.closePopup();
    },

    async onApproveChange(data) {
      try {
        const { APIApproveClockModificationCommand } = await import('@/modules/working_hours/infra/APIClockRepository');
        
        const cmd = new APIApproveClockModificationCommand();
        await cmd.execute(data.clockUuid, data.changeIndex, data.adminId, 'validada');
        
        await this.loadUserData();
        
      } catch (error) {
        throw error;
      }
    },

    async downloadCSV() {
      if (!this.selectedUser) return;

      // Obtener el ID del usuario autenticado desde el store
      const userData = this.$store.getters['me/userData'];
      const adminUserId = userData && userData.id;

      if (!adminUserId) {
        this.showSnackbar('error', 'No se pudo identificar el usuario actual');
        return;
      }

      this.screenData.ui_loading = true;

      try {
        const { sync_command_dispatcher } = await import('@/api/client');

        const year = this.screenData.currentYear;
        const month = this.screenData.currentMonth;
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0);

        const result = await sync_command_dispatcher.dispatch('generate_compliance_report', {
          admin_user_id: adminUserId,
          employee_user_id: this.selectedUser,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          format: 'csv'
        });
        
        // CAMBIO: Acceder a result.output en lugar de result directamente
        const data = result.output || result;
        
        if (!data || !data.csv_data) {
          throw new Error('El backend no devolvió datos CSV');
        }
        
        const blob = new Blob([data.csv_data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename || `informe_${this.selectedUser}_${year}_${month}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);

      } catch (error) {
        this.showSnackbar('error', 'Error al descargar el CSV: ' + error.message);
      } finally {
        this.screenData.ui_loading = false;
      }
    },

    async loadUsers() {
      try {
        await this.workingHoursComponent.load_users();


        // Primero intentar seleccionar el usuario actual desde el query param
        const userIdFromQuery = this.$route.query.userId;

        if (userIdFromQuery) {
          const userIdNum = parseInt(userIdFromQuery);
          const userInList = this.screenData.users.find(u => u.id === userIdNum);
          if (userInList) {
            this.selectedUser = userIdNum;
            this.workingHoursComponent.set_selected_user(this.selectedUser);
            await this.loadUserData();
            return;
          }
        }

        // Si no hay query param, intentar seleccionar el usuario autenticado
        const userData = this.$store.getters['me/userData'];
        const currentUserId = userData && userData.id;


        if (currentUserId) {
          // Verificar si el usuario actual está en la lista
          const currentUserInList = this.screenData.users.find(u => u.id === currentUserId);


          if (currentUserInList) {
            // Seleccionar automáticamente el usuario actual
            this.selectedUser = currentUserId;
            this.workingHoursComponent.set_selected_user(this.selectedUser);
            await this.loadUserData();
            return;
          } else {
          }
        }

        // Si no hay usuario en el store o no está en la lista, usar la lógica existente
        if (this.workingHoursComponent.has_single_user()) {
          this.selectedUser = this.workingHoursComponent.get_first_user_id();
          this.workingHoursComponent.set_selected_user(this.selectedUser);
          await this.loadUserData();
        } else if (this.screenData.users.length > 0) {
          // Si hay múltiples usuarios pero el autenticado no está, seleccionar el primero
          this.selectedUser = this.screenData.users[0].id;
          this.workingHoursComponent.set_selected_user(this.selectedUser);
          await this.loadUserData();
        }
      } catch (error) {
      }
    },

    loadCurrentUser() {
      // Obtener el usuario autenticado del store
      const userData = this.$store.getters['me/userData'];

      if (userData) {
        // El usuario del store tiene la estructura completa con user_role
        this.currentUser = userData;
        return;
      }

      // Fallback: intentar obtener el ID del usuario autenticado y buscarlo en la lista
      const authUserId = this.$store.state.me && this.$store.state.me.id;
      if (authUserId && this.screenData.users && this.screenData.users.length > 0) {
        const authenticatedUser = this.screenData.users.find(u => u.id === authUserId);
        if (authenticatedUser) {
          this.currentUser = authenticatedUser;
          return;
        }
      }

    },

    async loadUserData() {
      if (!this.selectedUser) return;

      this.screenData.ui_loading = true;
      try {
        this.workingHoursComponent.set_selected_user(this.selectedUser);
        await this.workingHoursComponent.load_user_data();
        await this.loadAbsences();
        await this.loadWorkSchedule();
      } catch (error) {
      } finally {
        this.screenData.ui_loading = false;
      }
    },

    async loadWorkSchedule() {
      try {
        if (!this.selectedUser) {
          this.screenData.workSchedule = null;
          return;
        }

        const { query_dispatcher } = await import('@/api/client');

        const result = await query_dispatcher.dispatch('get_work_schedule_by_user.query', {
          user_id: this.selectedUser
        });

        // El resultado puede venir en result.output o result directamente
        this.screenData.workSchedule = result.output || result;
      } catch (error) {
        console.error('Error al cargar horario de trabajo:', error);
        this.screenData.workSchedule = null;
      }
    },

    async loadAbsences() {
      try {
        if (!this.selectedUser) {
          this.screenData.absences = [];
          return;
        }

        // Usar filtros como lo hace el clockRepo
        const absences = await this.absenceRepo.find_by_criteria(
          [['user_id', '==', this.selectedUser]],
          ['-start'],
          undefined,
          undefined
        );

        this.screenData.absences = absences;
      } catch (error) {
        this.screenData.absences = [];
      }
    },

    // Verifica si un día específico tiene una ausencia/vacación APROBADA
    isDayWithApprovedAbsence(fechaStr) {
      if (!fechaStr || !this.screenData.absences) {
        return false;
      }

      const diaFecha = new Date(fechaStr);
      diaFecha.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(ausencia => {
        // Solo ausencias aprobadas
        if (ausencia.status !== 'approved') {
          return false;
        }

        const inicioAusencia = new Date(ausencia.start);
        const finAusencia = new Date(ausencia.end);
        inicioAusencia.setUTCHours(0, 0, 0, 0);
        finAusencia.setUTCHours(0, 0, 0, 0);

        return diaFecha >= inicioAusencia && diaFecha <= finAusencia;
      });
    },

    hasAbsenceOnDate(dateString) {
      if (!dateString || !this.screenData.absences) return false;

      const dayDate = new Date(dateString);
      dayDate.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(absence => {
        // Excluir ausencias/vacaciones rechazadas del calendario
        if (absence.status === 'rejected') return false;

        // Excluir bajas pendientes del calendario (solo mostrarlas en el widget superior)
        if (absence.tipoAusencia && absence.tipoAusencia.startsWith('Baja:') && absence.status === 'pending') {
          return false;
        }

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setUTCHours(0, 0, 0, 0);
        absenceEnd.setUTCHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    hasApprovedHolidayOnDate(dateString) {
      if (!dateString || !this.screenData.absences) return false;

      const dayDate = new Date(dateString);
      dayDate.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(absence => {
        // Solo vacaciones aprobadas
        if (absence.tipoAusencia !== 'Vacaciones') return false;
        if (absence.status !== 'approved') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setUTCHours(0, 0, 0, 0);
        absenceEnd.setUTCHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    hasPendingHolidayOnDate(dateString) {
      if (!dateString || !this.screenData.absences) return false;

      const dayDate = new Date(dateString);
      dayDate.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(absence => {
        // Solo vacaciones pendientes
        if (absence.tipoAusencia !== 'Vacaciones') return false;
        if (absence.status !== 'pending') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setUTCHours(0, 0, 0, 0);
        absenceEnd.setUTCHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    hasApprovedAbsenceOnDate(dateString) {
      if (!dateString || !this.screenData.absences) return false;

      const dayDate = new Date(dateString);
      dayDate.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(absence => {
        // Solo ausencias aprobadas que NO sean vacaciones
        if (absence.tipoAusencia === 'Vacaciones') return false;
        if (absence.status !== 'approved') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setUTCHours(0, 0, 0, 0);
        absenceEnd.setUTCHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    hasPendingAbsenceOnDate(dateString) {
      if (!dateString || !this.screenData.absences) return false;

      const dayDate = new Date(dateString);
      dayDate.setUTCHours(0, 0, 0, 0);

      return this.screenData.absences.some(absence => {
        // Solo ausencias pendientes que NO sean vacaciones
        if (absence.tipoAusencia === 'Vacaciones') return false;
        if (absence.status !== 'pending') return false;

        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setUTCHours(0, 0, 0, 0);
        absenceEnd.setUTCHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    getAbsencesForDate(dateString) {
      if (!dateString || !this.screenData.absences) return [];

      const dayDate = new Date(dateString);
      dayDate.setHours(0, 0, 0, 0);

      return this.screenData.absences.filter(absence => {
        const absenceStart = new Date(absence.start);
        const absenceEnd = new Date(absence.end);
        absenceStart.setHours(0, 0, 0, 0);
        absenceEnd.setHours(0, 0, 0, 0);

        return dayDate >= absenceStart && dayDate <= absenceEnd;
      });
    },

    /**
     * Determina el estado de las ausencias de un día para el indicador visual
     * - 'pending': Solo ausencias pendientes (amarillo)
     * - 'approved': Solo ausencias aprobadas (verde)
     * - 'mixed': Mezcla de pendientes y aprobadas (lila)
     * - null: No hay ausencias
     */
    getAbsenceStatusForDate(dateString) {
      const absences = this.getAbsencesForDate(dateString);

      if (absences.length === 0) return null;

      const hasPending = absences.some(absence => absence.status === 'pending');
      const hasApproved = absences.some(absence => absence.status === 'approved');

      if (hasPending && hasApproved) {
        return 'mixed'; // Lila
      } else if (hasPending) {
        return 'pending'; // Amarillo
      } else if (hasApproved) {
        return 'approved'; // Verde
      }

      return null;
    },

    async onUserChange() {
      this.workingHoursComponent.set_selected_user(this.selectedUser);
      await this.loadUserData();
    },

    // async onApproveChange(data) {
    //   try {
    //     const { ApproveClockModificationCommand } = await import('@/modules/working_hours/app/ApproveClockModificationCommand');
    //     const changeIndex = data.changeIndex;
        
    //     if (changeIndex < 0) {
    //       throw new Error(`Índice de cambio inválido: ${changeIndex}`);
    //     }

    //     const clockRepo = this.workingHoursComponent.clockRepo;
    //     const userRepo = this.workingHoursComponent.userRepo;
    //     const screen_data = { ui_loading: false, clocks: [], pending_modifications: [] };
    //     const cmd = new ApproveClockModificationCommand(clockRepo, screen_data, userRepo);
        
    //     await cmd.execute(data.clockUuid, changeIndex, data.adminId, 'validada');
        
    //     await this.loadUserData();
        
        
    //   } catch (error) {
    //     throw error;
    //   }
    // },

    openDayPopup(day) {
      if (!day.date) {
        return;
      }
      this.selectedDate = day.date;
      this.selectedDayClocks = this.workingHoursComponent.get_day_clocks(day.date);
      this.selectedDayAbsences = this.getAbsencesForDate(day.date);
      this.popupVisible = true;
    },

    closePopup() {
      this.popupVisible = false;
      this.selectedDate = null;
      this.selectedDayClocks = [];
    },

    async onClocksSaved() {
      await this.loadUserData();
    },

    async previousMonth() {
      this.workingHoursComponent.previous_month();
      await this.loadUserData();
    },

    async nextMonth() {
      this.workingHoursComponent.next_month();
      await this.loadUserData();
    },

    getMonthName(monthNumber) {
      return this.monthNames[monthNumber - 1];
    },

    getDayNumber(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.getDate();
    },

    isToday(dateString) {
      if (!dateString) return false;
      const today = new Date();
      const dayDate = new Date(dateString);
      return today.getDate() === dayDate.getDate() &&
             today.getMonth() === dayDate.getMonth() &&
             today.getFullYear() === dayDate.getFullYear();
    },

    formatHoursAndMinutes(totalMinutes) {
      if (!totalMinutes || totalMinutes <= 0) return '';
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours === 0) {
        return `${minutes}min`;
      } else if (minutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h y ${minutes}min`;
      }
    },

    formatOnlyDate(dateStr) {
      const date = new Date(dateStr);
      // Usar métodos UTC para evitar problemas de zona horaria
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${day}/${month}/${year}`;
    },

    async approveHoliday(holiday) {
      try {
        this.$set(holiday, 'approving', true);

        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: holiday.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'approved'
        });

        this.showSnackbar('success', 'Vacación aprobada correctamente');

        // Recargar las ausencias para actualizar la lista
        await this.loadAbsences();
      } catch (error) {
        console.error('Error al aprobar vacación:', error);
        this.showSnackbar('error', 'Error al aprobar la vacación');
      } finally {
        this.$set(holiday, 'approving', false);
      }
    },

    async rejectHoliday(holiday) {
      try {
        this.$set(holiday, 'rejecting', true);

        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: holiday.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'rejected'
        });

        this.showSnackbar('success', 'Vacación rechazada correctamente');

        // Recargar las ausencias para actualizar la lista
        await this.loadAbsences();
      } catch (error) {
        console.error('Error al rechazar vacación:', error);
        this.showSnackbar('error', 'Error al rechazar la vacación');
      } finally {
        this.$set(holiday, 'rejecting', false);
      }
    },

    async approveBaja(baja) {
      try {
        this.$set(baja, 'approving', true);

        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: baja.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'approved'
        });

        this.showSnackbar('success', 'Baja aprobada correctamente');

        // Recargar las ausencias para actualizar la lista
        await this.loadAbsences();
      } catch (error) {
        console.error('Error al aprobar baja:', error);
        this.showSnackbar('error', 'Error al aprobar la baja');
      } finally {
        this.$set(baja, 'approving', false);
      }
    },

    async rejectBaja(baja) {
      try {
        this.$set(baja, 'rejecting', true);

        const { sync_command_dispatcher } = await import('@/api/client');

        await sync_command_dispatcher.dispatch('approve_absence', {
          absence_uuid: baja.uuid,
          admin_user_id: this.currentUser.id,
          new_status: 'rejected'
        });

        this.showSnackbar('success', 'Baja rechazada correctamente');

        // Recargar las ausencias para actualizar la lista
        await this.loadAbsences();
      } catch (error) {
        console.error('Error al rechazar baja:', error);
        this.showSnackbar('error', 'Error al rechazar la baja');
      } finally {
        this.$set(baja, 'rejecting', false);
      }
    },

    formatBajaType(tipoAusencia) {
      // Eliminar el prefijo "Baja: " para mostrar solo el tipo
      if (!tipoAusencia) return 'Baja';
      return tipoAusencia.replace('Baja: ', '');
    },

    isIndefinidaBaja(baja) {
      return baja.tipoAusencia && (
        baja.tipoAusencia.includes('Cumplimiento deber inexcusable') ||
        baja.tipoAusencia.includes('Funciones sindicales') ||
        baja.tipoAusencia.includes('Nacimiento y cuidado del menor') ||
        baja.tipoAusencia.includes('Donación de órganos')
      );
    },

    formatBajaEndDate(baja) {
      // Si es una baja indefinida, mostrar "Indefinida"
      if (this.isIndefinidaBaja(baja)) {
        return 'Indefinida';
      }

      return this.formatOnlyDate(baja.end);
    },

    formatDateForPicker(dateStr) {
      // Convertir fecha ISO a formato YYYY-MM-DD para el date picker
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    onActiveBajaEndDateChange(newDate) {
      // newDate viene en formato YYYY-MM-DD
      this.$set(this.activeBaja, 'editedEndDatePicker', newDate);

      // Convertir a formato DD/MM/YYYY para mostrar
      const parts = newDate.split('-');
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      this.$set(this.activeBaja, 'editedEndDate', `${day}/${month}/${year}`);

      // Cerrar el date picker
      this.$set(this.activeBaja, 'showDatePicker', false);
    },

    async updateActiveBajaEndDate() {
      if (!this.activeBaja || !this.activeBaja.editedEndDatePicker) return;

      try {
        this.$set(this.activeBaja, 'updating', true);

        const { sync_command_dispatcher } = await import('@/api/client');

        // Convertir fecha del picker (YYYY-MM-DD) a ISO format con hora
        const endDate = new Date(this.activeBaja.editedEndDatePicker);
        endDate.setHours(23, 59, 59, 999);

        // Actualizar la fecha fin de la baja
        await sync_command_dispatcher.dispatch('update_absence', {
          uuid: this.activeBaja.uuid,
          user_id: this.activeBaja.user_id,
          start: this.activeBaja.start,
          end: endDate.toISOString(),
          reason: this.activeBaja.reason,
          fotoJustificante: this.activeBaja.fotoJustificante || null,
          lista_motivos: this.activeBaja.lista_motivos || null,
          tipoAusencia: this.activeBaja.tipoAusencia || null,
          status: this.activeBaja.status || 'approved',
        });

        this.showSnackbar('success', 'Fecha de baja actualizada correctamente');

        // Limpiar los campos editados
        this.$set(this.activeBaja, 'editedEndDate', null);
        this.$set(this.activeBaja, 'editedEndDatePicker', null);

        // Recargar las ausencias para actualizar la lista
        await this.loadAbsences();
      } catch (error) {
        console.error('Error al actualizar fecha de baja:', error);
        this.showSnackbar('error', 'Error al actualizar la fecha de baja');
      } finally {
        this.$set(this.activeBaja, 'updating', false);
      }
    }
  }
};
</script>

<style scoped>
.admin-month-view {
  background-color: #FFFFFF;
  min-height: calc(100vh - 60px);
  padding: 12px 12px 40px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-y: auto;
  border-radius: 20px 20px 0 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 4px;
}

.left-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 4px;
}

.page-title {
  margin-top: 2px;
  margin-left: 20px;
  font-size: 17px;
  font-weight: 600;
  color: var(--v-primaryDark-base);
}

.btn-volver {
  display: flex;
  align-items: center;
  color: var(--Neutral-300, #B0B0B0);
  font-family: Roboto;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 4px;
  width: 400px;
  height: 16px;
  flex-shrink: 0;
}

.icono-btn-secu {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-primario.btn-refresh {
  background: var(--Primary-700, #7339B7);
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-primario.btn-refresh:hover {
  background: #7a66d9;
}

.btn-primario.btn-refresh:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.icono-btn {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.header-separator {
  width: 100%;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0;
}

.header-separator-1 {
  width: 100%;
  border: none;
  border-top: 1px solid #ccc;
  margin: 0;
}

.acciones-secundarias {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  margin: 12px 0;
  padding: 0 12px;
  box-sizing: border-box;
}

.btn-secundario-1 {
  display: flex;
  width: auto;
  height: 26px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200, #D1D1D1);
  background: var(--Neutral-150, #F3F3F3);
  color: #666;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-secundario-1:hover {
  background: #ECECEC;
}

.btn-secundario {
  display: flex;
  width: 150px;
  height: 26px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200, #D1D1D1);
  background: var(--Neutral-150, #E7E7E7);
  color: #333;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}

/* Controles existentes reorganizados */
.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  gap: 16px;
  padding: 0 12px;
  box-sizing: border-box;
}

.user-selector {
  flex: 1;
  max-width: 300px;
}

.user-dropdown {
  background-color: white;
}

.month-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.month-nav-btn {
  background-color: var(--Primary-700, #7339B7) !important;
  color: white !important;
}

.month-nav-btn:hover {
  background-color: #7a66d9 !important;
}

.month-display {
  font-size: 14px;
  font-weight: 600;
  color: var(--Primary-700, #7339B7);
  min-width: 130px;
  text-align: center;
}

.section-separator {
  margin: 0 0 16px 0;
  border: none;
  border-top: 2px solid #e0e0e0;
  width: 100%;
}

/* Summary Widgets */
.summary-widgets-container {
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 20px;
  padding: 0 12px;
  box-sizing: border-box;
  align-items: stretch;
  justify-content: center;
  flex-wrap: wrap;
}

.full-width-widgets {
  max-width: 1200px;
  margin: 0 auto 20px auto;
}

.full-width-widgets .summary-widgets {
  flex: 1;
  max-width: none;
}

.full-width-widgets .admin-widgets-container {
  flex: 1;
  max-width: none;
}

.summary-widgets {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 auto;
  align-items: stretch;
}

.summary-widgets-horizontal {
  flex-direction: column !important;
}

.admin-widgets-container {
  display: flex;
  gap: 16px;
  flex: 1;
  align-items: stretch;
}

.admin-widgets-container .holidays-widget {
  flex: 1;
  min-width: 350px;
  display: flex;
  flex-direction: column;
}

/* Widget de baja activa */
.active-baja-widget {
  background: linear-gradient(135deg, #FFF5F5 0%, #FFEBEE 100%);
  border: 2px solid #F44336;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.15);
  flex: 0 0 auto;
  min-width: 300px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
}

.widget-header-baja-active {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(244, 67, 54, 0.2);
}

.widget-title-baja-active {
  font-size: 14px;
  font-weight: 600;
  color: #F44336;
  margin: 0;
}

.baja-active-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.baja-active-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.baja-active-label {
  font-weight: 600;
  color: #666;
  font-size: 12px;
}

.baja-active-value {
  font-weight: 500;
  color: #333;
  font-size: 12px;
}

.baja-active-status {
  margin-top: 10px;
  padding: 8px;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #F44336;
  font-weight: 500;
  font-size: 12px;
}

/* Estilos para edición de fecha de baja indefinida */
.editable-date-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.compact-date-field {
  max-width: 200px;
}

.compact-date-field >>> .v-input__control {
  min-height: 36px !important;
}

.compact-date-field >>> .v-text-field__details {
  display: none;
}

.indefinida-note {
  font-size: 11px;
  color: #F44336;
  font-style: italic;
  margin-top: 2px;
}

.update-baja-btn {
  margin-top: 16px;
  width: 100%;
}

.widget {
  flex: 1;
  min-height: 90px;
  min-width: 200px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.widget:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(115, 57, 183, 0.2);
}

.widget-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(115, 57, 183, 0.1);
  border-radius: 8px;
}

.widget-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.widget-label {
  font-size: 11px;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.widget-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--Primary-700, #7339B7);
}

/* Calendar styles */
.calendar-container {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 24px;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: var(--Primary-700, #7339B7);
  color: white;
}

.day-header {
  padding: 10px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
}

.calendar-body {
  display: flex;
  flex-direction: column;
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #e0e0e0;
}

.calendar-week:last-child {
  border-bottom: none;
}

.calendar-day {
  min-height: 80px;
  padding: 6px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: white;
  transition: all 0.2s ease;
}

.calendar-day:last-child {
  border-right: none;
}

.calendar-day.clickable-day {
  cursor: pointer;
}

.calendar-day.clickable-day:hover {
  background-color: #f0f0ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(115, 57, 183, 0.2);
}

.calendar-day.empty-day {
  background-color: var(--v-neutral-base);
  color: #ccc;
  cursor: default;
}

.calendar-day.holiday-approved-day {
  background-color: #ede4f7 !important;
  border: 2px solid #e1d7f1 !important;
  position: relative;
}

.calendar-day.holiday-approved-day::before {
  content: '🏖️';
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 16px;
  opacity: 0.7;
}

.calendar-day.holiday-approved-day:hover {
  background-color: #ede4f7 !important;
  transform: none;
  box-shadow: 0 2px 8px rgba(115, 57, 183, 0.15);
}

.calendar-day.empty-day:hover {
  background-color: var(--v-neutral-base);
  transform: none;
  box-shadow: none;
}

.calendar-day.current-day {
  border: 3px solid #7339B7;
}

.calendar-day.current-day .day-number {
  background-color: #7339B7;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin: 0 auto 4px auto;
}

.day-number {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
  color: #333;
}

.empty-day .day-number {
  color: #ccc;
}

.day-hours {
  font-size: 12px;
  font-weight: 600;
  color: var(--Primary-700, #7339B7);
  background-color: rgba(115, 57, 183, 0.1);
  padding: 2px 6px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 5px;
  align-self: flex-start;
}

.day-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.clock-entry {
  font-size: 10px;
  color: #666;
  background-color: #f0f0f0;
  padding: 1px 4px;
  border-radius: 4px;
  text-align: center;
}

.click-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: var(--Primary-700, #7339B7);
}

.calendar-day.clickable-day:hover .click-indicator {
  opacity: 1;
}

.absence-indicator {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
}

.absence-dot {
  width: 8px;
  height: 8px;
  background-color: #FF5252; /* Color por defecto (rojo) */
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(255, 82, 82, 0.5);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

/* Ausencia pendiente - Amarillo */
.absence-dot.absence-pending {
  background-color: #FFA726; /* Naranja/Amarillo */
  box-shadow: 0 0 6px rgba(255, 167, 38, 0.6);
}

/* Ausencia aprobada - Verde */
.absence-dot.absence-approved {
  background-color: #66BB6A; /* Verde */
  box-shadow: 0 0 6px rgba(102, 187, 106, 0.6);
}

/* Mezcla de aprobada y pendiente - Lila */
.absence-dot.absence-mixed {
  background-color: #AB47BC; /* Lila/Morado */
  box-shadow: 0 0 6px rgba(171, 71, 188, 0.6);
}

.absence-dot-holiday-approved {
  background-color: #10b981 !important;
  border: 2px solid #8E24AA !important;
  box-shadow: 0 0 4px rgba(16, 185, 129, 0.5) !important;
}

.absence-dot-holiday-pending {
  background-color: #f59e0b !important;
  border: 2px solid #8E24AA !important;
  box-shadow: 0 0 4px rgba(245, 158, 11, 0.5) !important;
}

.absence-dot-approved {
  background-color: #10b981 !important;
  box-shadow: 0 0 4px rgba(16, 185, 129, 0.5) !important;
}

.absence-dot-pending {
  background-color: #f59e0b !important;
  box-shadow: 0 0 4px rgba(245, 158, 11, 0.5) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
  }

  .left-section {
    width: 100%;
  }

  .right-section {
    width: 100%;
    justify-content: flex-end;
    gap: 16px;
  }

  .btn-volver {
    width: 100%;
    justify-content: flex-start;
  }

  .page-title {
    margin-left: 0;
    font-size: 15px;
  }

  .acciones-secundarias {
    justify-content: center;
    padding: 0;
    margin: 12px 0 0 0;
  }

  .header-controls {
    flex-direction: column;
    gap: 12px;
    padding: 0;
  }

  .user-selector {
    max-width: 100%;
  }

  .summary-widgets {
    flex-direction: column;
    gap: 12px;
  }

  .widget {
    max-width: 100%;
  }

  .calendar-day {
    min-height: 60px;
    padding: 4px;
  }

  .day-number {
    font-size: 11px;
  }

  .day-hours {
    font-size: 9px;
  }

  .clock-entry {
    font-size: 8px;
  }
}

@media (max-width: 480px) {
  .admin-month-view {
    padding: 8px;
  }

  .page-title {
    font-size: 14px;
  }

  .calendar-day {
    min-height: 50px;
    padding: 2px;
  }

  .day-header {
    padding: 8px 4px;
    font-size: 10px;
  }

  .btn-secundario {
    width: 100px;
  }

  .btn-secundario-1 {
    width: auto;
  }
}

.download-csv-btn {
  margin: 0 12px;
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  gap: 16px;
  padding: 0 12px;
  box-sizing: border-box;
}

/* Estilos del widget de vacaciones */
.holidays-widget {
  background-color: white;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex: 1;
  min-width: 300px;
  min-height: 250px;
  display: flex;
  flex-direction: column;
}

.widget-header-holidays {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid #E1D7F1;
}

.widget-title-holidays {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #7339B7;
}

.holidays-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.holiday-card {
  background-color: #FFFFFF;
  border-radius: 10px;
  padding: 10px 14px;
  border: 1px solid #E1D7F1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  transition: box-shadow 0.2s ease;
}

.holiday-card:hover {
  box-shadow: 0 4px 12px rgba(115, 57, 183, 0.15);
}

.holiday-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.holiday-row {
  display: flex;
  gap: 8px;
  align-items: baseline;
}

.holiday-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  min-width: 50px;
}

.holiday-value {
  color: #374151;
  font-size: 12px;
  font-weight: 500;
}

.holiday-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  min-width: 100px;
}

.no-holidays-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 12px;
  color: #6b7280;
  text-align: center;
  gap: 10px;
}

.no-holidays-message p {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

@media (max-width: 768px) {
  .summary-widgets-container {
    flex-direction: column;
  }

  .holidays-widget {
    min-width: unset;
    max-width: unset;
    width: 100%;
  }

  .holiday-card {
    flex-direction: column;
    align-items: stretch;
  }

  .holiday-actions {
    width: 100%;
    justify-content: center;
  }

  .action-btn {
    width: 100%;
    max-width: 200px;
  }
}
</style>